"use strict";
/* globals module */
/**
 * @module articleCommentRepliesClient
 * @description  Article Comment Replies Client provides an easy way to consume  Article Comment Replies REST API end-points. `articleCommentRepliesService` functions enable performing standard CRUD operations directly on article comment reply resources, whereas the `articleClient` functions allow management between article and article comment reply. In order to obtain needed routes `articleCommentRepliesService` uses `articleCommentRepliesRoute`.
*/
Object.defineProperty(exports, "__esModule", { value: true });
var tslib_1 = require("tslib");
var inversify_1 = require("inversify");
;
var httpApi_1 = require("../../httpApi");
var _1 = require("./");
var ArticleCommentRepliesClient = /** @class */ (function () {
    function ArticleCommentRepliesClient(articleCommentRepliesRoute, apiClient) {
        this.articleCommentRepliesRoute = articleCommentRepliesRoute;
        this.apiClient = apiClient;
        /**
         * Contains a reference to valid list of article comment reply states. It returns an object containing all article comment reply states.
         * @method
         * @example articleCommentRepliesClient.statuses.approved;
         **/
        this.statuses = _1.CommentStatus;
    }
    Object.defineProperty(ArticleCommentRepliesClient.prototype, "routeDefinition", {
        /**
         * Provides direct access to `articleCommentRepliesRoute`.
         * @method
         * @example articleCommentRepliesClient.routeDefinition.get();
         **/
        get: function () {
            return this.articleCommentRepliesRoute;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Returns a promise that is resolved once the approve article comment reply action has been performed. This action sets the state of an article comment reply to "approved". This route uses HAL enabled objects to obtain routes and therefore it doesn't apply `articleCommentRepliesRoute` route template. Here is an example of how a route can be obtained from HAL enabled objects:
     * ```
     * let params = modelMapper.updateParams(articleCommentReply);
     * let uri = params['model'].links('comment-approve').href;
     * ```
     * @method
     * @param data Article Comment Reply object.
     * @param options Notification configuration used to control the article comment recourse access when managing notification distribution.
     * @returns A promise that is resolved once the approve article comment reply action has been performed.
     * @example // articleCommentReply is a resource previously fetched using get action.
                    articleCommentRepliesClient.approve(articleCommentReply, commentOptions)
                        .then(function (data) {
                            // perform success action here
                        },
                         function (response, status, headers, config) {
                            // perform error handling here
                        });
     **/
    ArticleCommentRepliesClient.prototype.approve = function (data, options) {
        return this.apiClient.put(this.routeDefinition.approve(data), this.routeDefinition.updateParams(data));
    };
    /**
     * Returns a promise that is resolved once the unapprove article comment reply action has been performed. This action sets the state of an article comment reply to "unapproved". This route uses HAL enabled objects to obtain routes and therefore it doesn't apply `articleCommentRepliesRoute` route template. Here is an example of how a route can be obtained from HAL enabled objects:
     * ```
     * let params = modelMapper.updateParams(articleCommentReply);
     * let uri = params['model'].links('comment-unapprove').href;
     * ```
     * @method
     * @param data Article Comment Reply object.
     * @returns A promise that is resolved once the unapprove article comment reply action has been performed.
     * @example // articleCommentReply is a resource previously fetched using get action.
                    articleCommentRepliesClient.unapprove(articleCommentReply)
                        .then(function (data) {
                            // perform success action here
                        },
                         function (response, status, headers, config) {
                            // perform error handling here
                        });
     **/
    ArticleCommentRepliesClient.prototype.unapprove = function (data) {
        return this.apiClient.put(this.routeDefinition.unapprove(data), this.routeDefinition.updateParams(data));
    };
    /**
     * Returns a promise that is resolved once the create article comment reply action has been performed; this action creates a new comment reply for an article.
     * @method
     * @param data An article comment reply object that needs to be inserted into the system.
     * @returns A promise that is resolved once the create article comment reply action has been performed.
     * @example articleCommentRepliesClient.create('<article-id>', {
                    commentId : '<comment-id>',
                    comment : <comment>,
                    userId : '<user-id>' })
                .then(function (data) {
                    // perform success action here
                },
                 function (response, status, headers, config) {
                    // perform error handling here
                });
     **/
    ArticleCommentRepliesClient.prototype.create = function (data) {
        return this.apiClient.post(this.routeDefinition.create(data), this.routeDefinition.createParams(data));
    };
    /**
     * Returns a promise that is resolved once the find action has been performed. Success response returns a list of article comment reply resources matching the given criteria.
     * @method
     * @param options Query resource options object.
     * @returns A promise that is resolved once the find action has been performed.
     * @example articleCommentRepliesClient.find({
                    pageNumber : 1,
                    pageSize : 10,
                    orderBy : '<field>',
                    orderDirection : '<asc|desc>',
                    search : '<search-phrase>'
                })
                .then(function (collection) {
                    // perform success action here
                },
                 function (response, status, headers, config) {
                    // perform error handling here
                });
     **/
    ArticleCommentRepliesClient.prototype.find = function (options) {
        return this.apiClient.get(this.routeDefinition.find(options));
    };
    /**
     * Returns a promise that is resolved once the flag article comment reply action has been performed. This action sets the state of an article comment reply to "flagged". This route uses HAL enabled objects to obtain routes and therefore it doesn't apply `articleCommentRepliesRoute` route template. Here is an example of how a route can be obtained from HAL enabled objects:
     * ```
     * let params = modelMapper.updateParams(articleCommentReply);
     * let uri = params['model'].links('comment-flag').href;
     * ```
     * @method
     * @param data Article Comment Reply object.
     * @returns A promise that is resolved once the flag article comment reply action has been performed.
     * @example     // articleCommentReply is a resource previously fetched using get action.
                        articleCommentRepliesClient.flag(articleCommentReply)
                            .then(function (data) {
                                // perform success action here
                            },
                             function (response, status, headers, config) {
                                // perform error handling here
                            });
     **/
    ArticleCommentRepliesClient.prototype.flag = function (data) {
        return this.apiClient.put(this.routeDefinition.flag(data), this.routeDefinition.updateParams(data));
    };
    /**
     * Returns a promise that is resolved once the unflag article comment reply action has been performed. This action removes the "flagged" comment reply state. This route uses HAL enabled objects to obtain routes and therefore it doesn't apply `articleCommentRepliesRoute` route template. Here is an example of how a route can be obtained from HAL enabled objects:
     * ```
     * let params = modelMapper.updateParams(articleCommentReply);
     * let uri = params['model'].links('comment-unflag').href;
     * ```
     * @method
     * @param data Article Comment Reply object.
     * @returns A promise that is resolved once the unflag article comment reply action has been performed.
     * @example // articleCommentReply is a resource previously fetched using get action.
                    articleCommentRepliesClient.unflag(articleCommentReply)
                        .success(function (data) {
                            // perform success action here
                        }).error(function (response, status, headers, config) {
                            // perform error handling here
                        });
     **/
    ArticleCommentRepliesClient.prototype.unflag = function (data) {
        return this.apiClient.put(this.routeDefinition.unflag(data), this.routeDefinition.updateParams(data));
    };
    /**
     * Returns a promise that is resolved once the get action has been performed. Success response returns the specified article comment reply resource.
     * @method
     * @param id Id which uniquely identifies article comment reply resource that needs to be retrieved.
     * @param options Options object that contains embed data.
     * @returns A promise that is resolved once the get action has been performed.
     * @example articleCommentRepliesClient.get('<comment-reply-id>')
                    .then(function (data) {
                        // perform success action here
                    },
                     function (response, status, headers, config) {
                         // perform error handling here
                    });
     **/
    ArticleCommentRepliesClient.prototype.get = function (id, options) {
        return this.apiClient.get(this.articleCommentRepliesRoute.get(id, options));
    };
    /**
     * Returns a promise that is resolved once the remove article comment reply action has been performed. If the action is successfully completed, the article comment reply resource will be permanently removed from the system. This route uses HAL enabled objects to obtain routes and therefore it doesn't apply `articleCommentRepliesRoute` route template. Here is an example of how a route can be obtained from HAL enabled objects:
     * ```
     * let params = apiClient.removeParams(articleCommentReply);
     * let uri = params['model'].links('delete').href;
     * ```
     * @method
     * @param data An article Comment Reply object used to update specified article comment reply resource.
     * @returns A promise that is resolved once the remove article comment reply action has been performed.
     * @example // articleCommentReply is a resource previously fetched using get action.
                    articleCommentRepliesClient.remove(articleCommentReply)
                        .then(function (data) {
                            // perform success action here
                        },
                         function (response, status, headers, config) {
                            // perform error handling here
                        });
     **/
    ArticleCommentRepliesClient.prototype.remove = function (data) {
        return this.apiClient.delete(this.routeDefinition.delete(data));
    };
    /**
     * Returns a promise that is resolved once the report article comment reply action has been performed. This action sets the state of an article comment reply to "reported". This route uses HAL enabled objects to obtain routes and therefore it doesn't apply `articleCommentRepliesRoute` route template. Here is an example of how a route can be obtained from HAL enabled objects:
     * ```
     * let params = modelMapper.updateParams(articleCommentReply);
     * let uri = params['model'].links('comment-report').href;
     * ```
     * @method
     * @param data Article Comment Reply object.
     * @param options Notification configuration used to control the article comment recourse access when managing notification distribution.
     * @returns A promise that is resolved once the report article comment reply action has been performed.
     * @example // articleCommentReply is a resource previously fetched using get action.
                    articleCommentRepliesClient.report(articleCommentReply, commentOptions)
                        .then(function (data) {
                            // perform success action here
                        },
                         function (response, status, headers, config) {
                            // perform error handling here
                        });
     **/
    ArticleCommentRepliesClient.prototype.report = function (data, options) {
        return this.apiClient.put(this.routeDefinition.report(data), this.routeDefinition.updateParams(options));
    };
    /**
     * Returns a promise that is resolved once the unreport article comment reply action has been performed. This action removes the "reported" comment reply state. This route uses HAL enabled objects to obtain routes and therefore it doesn't apply `articleCommentRepliesRoute` route template. Here is an example of how a route can be obtained from HAL enabled objects:
     * ```
     * let params = modelMapper.updateParams(articleCommentReply);
     * let uri = params['model'].links('comment-unreport').href;
     * ```
     * @method
     * @param data Article Comment Reply object.
     * @returns A promise that is resolved once the unreport article comment reply action has been performed.
     * @example // articleCommentReply is a resource previously fetched using get action.
                    articleCommentRepliesClient.unreport(articleCommentReply)
                        .then(function (data) {
                            // perform success action here
                        },
                         function (response, status, headers, config) {
                            // perform error handling here
                        });
     **/
    ArticleCommentRepliesClient.prototype.unreport = function (data) {
        return this.apiClient.put(this.routeDefinition.unreport(data), this.routeDefinition.updateParams(data));
    };
    /**
     * Returns a promise that is resolved once the mark as spam article comment reply action has been performed. This action sets the state of an article comment reply to "spam". This route uses HAL enabled objects to obtain routes and therefore it doesn't apply `articleCommentRepliesRoute` route template. Here is an example of how a route can be obtained from HAL enabled objects:
     * ```
     * let params = modelMapper.updateParams(articleCommentReply);
     * let uri = params['model'].links('comment-spam').href;
     * ```
     * @method
     * @param data Article Comment Reply object.
     * @returns A promise that is resolved once the mark as spam article comment reply action has been performed.
     * @example // articleCommentReply is a resource previously fetched using get action.
                    articleCommentRepliesClient.spam(articleCommentReply)
                        .then(function (data) {
                            // perform success action here
                        },
                         function (response, status, headers, config) {
                            // perform error handling here
                        });
     **/
    ArticleCommentRepliesClient.prototype.spam = function (data) {
        return this.apiClient.put(this.routeDefinition.spam(data), this.routeDefinition.updateParams(data));
    };
    /**
     * Returns a promise that is resolved once the unspam article comment reply action has been performed. This action removes the "spam" comment reply state. This route uses HAL enabled objects to obtain routes and therefore it doesn't apply `articleCommentRepliesRoute` route template. Here is an example of how a route can be obtained from HAL enabled objects:
     * ```
     * let params = modelMapper.updateParams(articleCommentReply);
     * let uri = params['model'].links('comment-unspam').href;
     * ```
     * @method
     * @param data Article Comment Reply object.
     * @returns A promise that is resolved once the unspam article comment reply action has been performed.
     * @example // articleCommentReply is a resource previously fetched using get action.
                      articleCommentRepliesClient.unspam(articleCommentReply)
                        .then(function (data) {
                            // perform success action here
                        },
                         function (response, status, headers, config) {
                            // perform error handling here
                        });
     **/
    ArticleCommentRepliesClient.prototype.unspam = function (data) {
        return this.apiClient.put(this.routeDefinition.unspam(data), this.routeDefinition.updateParams(data));
    };
    /**
     * Returns a promise that is resolved once the update article comment reply action has been performed; this action updates an article comment reply resource. This route uses HAL enabled objects to obtain routes and therefore it doesn't apply `articleCommentRepliesRoute` route template. Here is an example of how a route can be obtained from HAL enabled objects:
     * ```
     * let params = modelMapper.updateParams(articleCommentReply);
     * let uri = params['model'].links('put').href;
     * ```
     * @method
     * @param data An Article Comments Reply object used to update specified article comment reply resource.
     * @returns A promise that is resolved once the update article comment reply action has been performed.
     * @example // articleCommentReply is a resource previously fetched using get action.
                    articleCommentRepliesClient.update(articleCommentReply)
                        .then(function (data) {
                            // perform success action here
                        },
                         function (response, status, headers, config) {
                            // perform error handling here
                        });
     **/
    ArticleCommentRepliesClient.prototype.update = function (data) {
        return this.apiClient.put(this.routeDefinition.update(data), this.routeDefinition.updateParams(data));
    };
    var _a, _b;
    ArticleCommentRepliesClient = tslib_1.__decorate([
        inversify_1.injectable(),
        tslib_1.__param(0, inversify_1.inject(_1.TYPES.ArticleCommentRepliesRoute)),
        tslib_1.__param(1, inversify_1.inject(httpApi_1.httpTYPES.ApiClient)),
        tslib_1.__metadata("design:paramtypes", [typeof (_a = typeof _1.ArticleCommentRepliesRoute !== "undefined" && _1.ArticleCommentRepliesRoute) === "function" ? _a : Object, typeof (_b = typeof httpApi_1.ApiClient !== "undefined" && httpApi_1.ApiClient) === "function" ? _b : Object])
    ], ArticleCommentRepliesClient);
    return ArticleCommentRepliesClient;
}());
exports.ArticleCommentRepliesClient = ArticleCommentRepliesClient;
/**
 * @overview
 ***Notes:**
 - Refer to the [REST API documentation](https://github.com/Baasic/baasic-rest-api/wiki) for detailed information about available Baasic REST API end-points.
 - All end-point objects are transformed by the associated route service.
 */ 
